import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import {
  filtrosToRpc,
  TAMANO_PAGINA,
  type AccionTramite,
  type BandejaFiltros,
  type BandejaResultado,
  type DetalleTramite,
  type TipoTramiteAdmin,
} from '../types/tramiteGremio';

/**
 * Buzón de trámites del gremio (ADR #051). Todo va por RPC: el filtrado, la búsqueda y el
 * orden se resuelven en la base y un operador puede ver el nombre de quien atendió.
 */
export const QK_BANDEJA = {
  all: ['bandeja-tramites'] as const,
  lista: (filtros: Record<string, unknown>, pagina: number) => ['bandeja-tramites', 'lista', filtros, pagina] as const,
  detalle: (id?: string) => ['bandeja-tramites', 'detalle', id] as const,
};

export function useBandejaTramites(filtros: BandejaFiltros) {
  const rpcFiltros = filtrosToRpc(filtros);
  return useQuery({
    queryKey: QK_BANDEJA.lista(rpcFiltros, filtros.pagina),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('bandeja_tramites', {
        p_filtros: rpcFiltros,
        p_limite: TAMANO_PAGINA,
        p_desde: (filtros.pagina - 1) * TAMANO_PAGINA,
      });
      if (error) throw new Error(error.message);
      return data as BandejaResultado;
    },
    placeholderData: (prev) => prev, // sin parpadeo al cambiar un filtro
    refetchInterval: 60_000, // la bandeja se actualiza sola cada minuto
  });
}

export function useDetalleTramite(id: string | undefined) {
  return useQuery({
    queryKey: QK_BANDEJA.detalle(id),
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('detalle_tramite', { p_tramite_id: id });
      if (error) throw new Error(error.message);
      return data as DetalleTramite;
    },
  });
}

/** Tipos de trámite (incluye inactivos) para el filtro. */
export function useTiposTramite() {
  return useQuery({
    queryKey: ['bandeja-tramites', 'tipos'],
    queryFn: async () => {
      const { data, error } = await supabase.from('tramite_tipos').select('id, codigo, nombre, activo, orden').order('orden');
      if (error) throw new Error(error.message);
      return (data ?? []) as TipoTramiteAdmin[];
    },
  });
}

export interface GestionarTramiteArgs {
  tramiteId: string;
  accion: AccionTramite;
  mensaje?: string;
}

export function useGestionarTramite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tramiteId, accion, mensaje }: GestionarTramiteArgs) => {
      const { data, error } = await supabase.rpc('gestionar_tramite', {
        p_tramite_id: tramiteId,
        p_accion: accion,
        p_mensaje: mensaje?.trim() || null,
      });
      if (error) throw new Error(error.message);
      return data as { id: string; estado: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_BANDEJA.all }),
  });
}

/** URL firmada (5 minutos) para abrir un adjunto del trámite. */
export async function urlAdjuntoTramite(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from('tramites-adjuntos').createSignedUrl(path, 300);
  if (error || !data?.signedUrl) throw new Error(error?.message ?? 'No se pudo abrir el archivo.');
  return data.signedUrl;
}
